import { OrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { toCreatedOrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/transformers/created-order-guide-transformer';
import { useCallback } from 'react';
import { useCreateSalesPortalOrderGuideCollectionMutation } from '@/experiences/sales-portal/domain/order-guides/blocs/documents/create-order-guide-collection.generated';

interface UseCreateOrderGuideCollectionMutatorOptions {
  onError?: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseCreateOrderGuideCollectionMutatorResult {
  /** Creates an empty named collection; resolves to the new (empty) guide, or `null` on failure. */
  createOrderGuide: (name: string) => Promise<OrderGuide | null>;
  loading: boolean;
}

/**
 * Creates a new empty order guide collection from scratch.
 *
 * @param options.onError - Called when creation fails. Lets the consumer
 * decide how to surface the error.
 * @param options.restaurantUUID - Restaurant the collection belongs to.
 * @param options.supplierUUID - Supplier the collection belongs to.
 * @returns the createOrderGuide function (resolving to the new empty guide or
 * `null` on failure) and its loading state
 */
export const useCreateOrderGuideCollectionMutator = ({
  onError,
  restaurantUUID,
  supplierUUID,
}: UseCreateOrderGuideCollectionMutatorOptions): UseCreateOrderGuideCollectionMutatorResult => {
  const [createCollection, { loading }] =
    useCreateSalesPortalOrderGuideCollectionMutation();

  const createOrderGuide = useCallback(
    async (name: string) => {
      try {
        const { data } = await createCollection({
          variables: { name, restaurantUUID, supplierUUID },
        });
        const collection =
          data?.createOrderGuideCollection.order_guide_restaurant_collection;
        if (!collection) {
          onError?.();
          return null;
        }
        return toCreatedOrderGuide(collection);
      } catch {
        onError?.();
        return null;
      }
    },
    [createCollection, onError, restaurantUUID, supplierUUID],
  );

  return { createOrderGuide, loading };
};
